import React, { useMemo, useState, useCallback } from 'react';
import { Button, Modal, Form, Input, Select, message } from 'antd';
import s from './s.module.less';
import type { PayoutAccount } from 'types/paymentSetup';
import { trackHeapEvent } from 'utils/analytics';
import { editPayoutAccount } from 'api/paymentSetup';
import StateSelect from 'components/form/StateSelect';

const { Option } = Select;

const TAX_ID_TYPE_OPTIONS = [
    { value: 'EIN', label: 'EIN' },
    { value: 'SSN', label: 'SSN' },
];

type Props = Readonly<{
    open: boolean;
    onClose: () => void;
    initialValue: PayoutAccount;
    onSaved: () => void;
}>;

export default function PayoutSetupModal({ open, onClose, initialValue, onSaved }: Props) {
    const [basicForm] = Form.useForm();
    const [accountForm] = Form.useForm();
    const [saving, setSaving] = useState(false);

    const initBasic = useMemo(
        () => ({
            firstName: initialValue?.firstName || '',
            lastName: initialValue?.lastName || '',
            address: (initialValue as any)?.address || '',
            city: (initialValue as any)?.city || '',
            state: (initialValue as any)?.state || '',
            zip: (initialValue as any)?.zip || '',
            email: (initialValue as any)?.email || '',
        }),
        [initialValue],
    );

    const initAccount = useMemo(
        () => ({
            companyName: (initialValue as any)?.companyName || '',
            taxIdType: (initialValue as any)?.taxIdType || 'EIN',
            taxId: (initialValue as any)?.taxId || '',
            accountNumber: initialValue?.accountNumber || '',
            routingNumber: initialValue?.routingNumber || '',
        }),
        [initialValue],
    );

    const handleAfterOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (nextOpen) {
                basicForm.setFieldsValue(initBasic);
                accountForm.setFieldsValue(initAccount);
            } else {
                basicForm.resetFields();
                accountForm.resetFields();
                setSaving(false);
            }
        },
        [accountForm, basicForm, initAccount, initBasic],
    );

    const handleSave = async () => {
        try {
            const basic = await basicForm.validateFields();
            const account = await accountForm.validateFields();
            setSaving(true);

            trackHeapEvent('WebApp - PPA Advance Setting Review - Click - Save Payout Setup Modal');
            await editPayoutAccount({
                ...initialValue,
                ...basic,
                ...account,
            });

            message.success('Payout account saved');
            onSaved();
            onClose();
        } catch (err) {
            console.error('Save payout setup modal failed:', err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            width={912}
            centered
            destroyOnClose
            onCancel={onClose}
            title="Set up payout account"
            className={s.payoutModal}
            afterOpenChange={handleAfterOpenChange}
            footer={
                <div className={s.payoutModalFooter}>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="primary" onClick={handleSave} loading={saving}>
                        Save
                    </Button>
                </div>
            }
        >
            <div className={s.payoutModalBody}>
                <div className={s.payoutModalSection}>
                    <div className={s.payoutModalSectionHeader}>
                        <div className={s.payoutModalSectionTitle}>Basic information</div>
                    </div>
                    <Form form={basicForm} layout="vertical">
                        <div className={s.formRow}>
                            <Form.Item label="First name" name="firstName" className={s.formCol}>
                                <Input placeholder="" />
                            </Form.Item>
                            <Form.Item label="Last name" name="lastName" className={s.formCol}>
                                <Input placeholder="" />
                            </Form.Item>
                        </div>
                        <div className={s.formRow}>
                            <div className={s.addressCol}>
                                <Form.Item
                                    label={
                                        <span>
                                            Address <span className={s.required}>*</span>
                                        </span>
                                    }
                                    name="address"
                                    rules={[{ required: true, message: 'Please enter address' }]}
                                >
                                    <Input placeholder="" />
                                </Form.Item>
                                <div className={s.addressSubRow}>
                                    <Form.Item
                                        label={
                                            <span>
                                                City <span className={s.required}>*</span>
                                            </span>
                                        }
                                        name="city"
                                        rules={[{ required: true, message: 'Please enter city' }]}
                                        className={s.cityField}
                                    >
                                        <Input placeholder="" />
                                    </Form.Item>
                                    <Form.Item
                                        label={
                                            <span>
                                                State <span className={s.required}>*</span>
                                            </span>
                                        }
                                        name="state"
                                        rules={[{ required: true, message: 'Please select state' }]}
                                        className={s.stateField}
                                    >
                                        <StateSelect />
                                    </Form.Item>
                                    <Form.Item
                                        label={
                                            <span>
                                                ZIP code <span className={s.required}>*</span>
                                            </span>
                                        }
                                        name="zip"
                                        rules={[{ required: true, message: 'Please enter ZIP' }]}
                                        className={s.zipField}
                                    >
                                        <Input placeholder="" />
                                    </Form.Item>
                                </div>
                            </div>
                            <Form.Item label="Email address" name="email" className={s.formCol}>
                                <Input placeholder="Enter your email address" />
                            </Form.Item>
                        </div>
                    </Form>
                </div>

                <div className={s.payoutModalSection}>
                    <div className={s.payoutModalSectionHeader}>
                        <div className={s.payoutModalSectionTitle}>Account info</div>
                    </div>
                    <Form form={accountForm} layout="vertical">
                        <Form.Item label="Company name" name="companyName" className={s.halfField}>
                            <Input placeholder="" />
                        </Form.Item>
                        <div className={s.formRow}>
                            <Form.Item label="Tax ID type" name="taxIdType" className={s.formCol}>
                                <Select>
                                    {TAX_ID_TYPE_OPTIONS.map((opt) => (
                                        <Option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item label="Tax ID" name="taxId" className={s.formCol}>
                                <Input placeholder="" />
                            </Form.Item>
                        </div>
                        <div className={s.formRow}>
                            <Form.Item label="Account number" name="accountNumber" className={s.formCol}>
                                <Input placeholder="" />
                            </Form.Item>
                            <Form.Item label="Routing number" name="routingNumber" className={s.formCol}>
                                <Input placeholder="" />
                            </Form.Item>
                        </div>
                    </Form>
                </div>
            </div>
        </Modal>
    );
}
